import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { studio } from '../data';
import { useLang } from '../hooks/useLang';
import { cn } from '../lib/cn';
import { saveNewsletter } from '../lib/storage';
import { LanguageSwitcher } from './LanguageSwitcher';

export function Footer() {
  const { t } = useTranslation();
  const lang = useLang();
  const year = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setStatus('error');
      return;
    }
    saveNewsletter(email.trim());
    setStatus('success');
    setEmail('');
  };

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${studio.address.street}, ${studio.address.zip} ${studio.address.city}`,
  )}`;

  return (
    <footer className="relative bg-ink-base border-t border-ink-line/50 px-6 py-14 md:px-10 md:py-20">
      <div className="mx-auto max-w-[1800px]">
      <div className="grid gap-12 md:grid-cols-12">
        <div className="md:col-span-5">
          <p className="text-base font-display lowercase text-ink-muted tracking-tight">
            {t('brand').toLowerCase()}, {studio.address.city.toLowerCase()}
          </p>
          <p className="mt-6 max-w-md text-base md:text-lg text-ink-paper leading-tight">
            {studio.tagline[lang]}
          </p>
        </div>

        <div className="md:col-span-3 grid content-start gap-3 text-sm">
          <p className="text-sm font-display lowercase text-ink-muted tracking-tight">
            {t('footer.sections.studio')}
          </p>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="hover:text-ink-paper transition-colors"
          >
            {studio.address.street}
            <br />
            {studio.address.zip} {studio.address.city}
          </a>
          <a href={`mailto:${studio.email}`} className="hover:text-ink-paper transition-colors">
            {studio.email}
          </a>
          <a
            href={`tel:${studio.phone.replace(/\s/g, '')}`}
            className="hover:text-ink-paper transition-colors"
          >
            {studio.phone}
          </a>
        </div>

        <div className="md:col-span-4 grid content-start gap-4 text-sm">
          <p className="text-sm font-display lowercase text-ink-muted tracking-tight">
            {t('footer.sections.newsletter')}
          </p>
          <p className="text-ink-paper/75 max-w-sm">{t('footer.newsletter.lead')}</p>
          <form onSubmit={onSubmit} className="flex border-b border-ink-line/60">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status !== 'idle') setStatus('idle');
              }}
              placeholder={t('footer.newsletter.placeholder')}
              className="flex-1 bg-transparent py-3 px-0 text-base text-ink-paper placeholder:text-ink-muted/70 focus:outline-none"
            />
            <button
              type="submit"
              className="text-sm font-display lowercase tracking-tight px-3 hover:text-ink-paper transition-colors"
            >
              {t('footer.newsletter.submit')} →
            </button>
          </form>
          <p
            aria-live="polite"
            className={cn(
              'text-sm font-display lowercase tracking-tight h-4',
              status === 'success' && 'text-ink-paper',
              status === 'error' && 'text-ink-accent',
              status === 'idle' && 'text-transparent',
            )}
          >
            {status === 'success' && t('footer.newsletter.success')}
            {status === 'error' && t('footer.newsletter.error')}
            {status === 'idle' && ' '}
          </p>
          <a
            href={`https://instagram.com/${studio.instagram}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-display lowercase tracking-tight text-ink-muted hover:text-ink-paper transition-colors w-fit"
          >
            @{studio.instagram} ↗
          </a>
        </div>
      </div>

      <div className="mt-16 flex flex-col gap-4 md:flex-row md:items-center md:justify-between border-t border-ink-line/40 pt-6">
        <p className="text-sm font-display lowercase tracking-tight text-ink-muted">
          © {year} {studio.legalName}. {t('footer.rights')}
        </p>
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-display lowercase tracking-tight text-ink-muted">
          <li>
            <Link to="/privacy" className="hover:text-ink-paper transition-colors">
              {t('footer.legal.privacy')}
            </Link>
          </li>
          <li>
            <a
              href={`mailto:${studio.email}`}
              className="hover:text-ink-paper transition-colors"
            >
              P.IVA 09813480964
            </a>
          </li>
        </ul>
        <LanguageSwitcher />
      </div>
      </div>
    </footer>
  );
}
